/**
 * Inner payment surface for the overlay widget — served on the Dorsia booking
 * origin at `/embed/pay` and framed by the overlay's `EmbeddedPaymentHost`.
 *
 * Stripe.js refuses to mount Elements in a shadow root, so the overlay can't
 * render the card fields itself. This page renders them in its own light-DOM
 * document (Stripe is happy), confirms the SetupIntent the host minted, and posts
 * the tokenized payment-method id back. It never receives the bearer token and
 * makes no Dorsia API calls — it only talks to Stripe and to its parent.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import Config from '@/lib/Config.ts';
import { CardFields, STRIPE_OPTIONS, type CardSubmitArgs } from './CardForm.tsx';
import {
  EMBED_PAY_SOURCE,
  parseEmbedPayMessage,
  type InitMessage,
  type EmbedPayMessage,
} from './embedPaymentMessages.ts';

interface FrameParams {
  parentOrigin: string;
  nonce: string;
}

const readParams = (): FrameParams | null => {
  const params = new URLSearchParams(window.location.search);
  const parentOrigin = params.get('parentOrigin');
  const nonce = params.get('nonce');
  if (!parentOrigin || !nonce) return null;
  // parentOrigin must be a bare origin (scheme://host[:port]); reject anything else.
  try {
    const url = new URL(parentOrigin);
    if (url.origin !== parentOrigin) return null;
  } catch {
    return null;
  }
  return { parentOrigin, nonce };
};

const EmbeddedPaymentFrame: React.FC = () => {
  const paramsRef = useRef<FrameParams | null>(readParams());
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [init, setInit] = useState<InitMessage | null>(null);
  const [fatal, setFatal] = useState<string | null>(paramsRef.current ? null : 'Invalid payment session.');
  const containerRef = useRef<HTMLDivElement>(null);

  const post = useCallback((message: EmbedPayMessage) => {
    const params = paramsRef.current;
    if (!params) return;
    window.parent.postMessage(message, params.parentOrigin);
  }, []);

  // A fatal (non-recoverable) failure: show it inline in the frame AND tell the
  // host so it can offer a Retry (re-mint + re-frame). `post()` no-ops when the
  // parent origin is unknown (missing URL params) — the host's ready-timeout is
  // the backstop for that case.
  const reportFatal = useCallback(
    (message: string) => {
      setFatal(message);
      const params = paramsRef.current;
      if (!params) return;
      post({
        source: EMBED_PAY_SOURCE,
        type: 'error',
        nonce: params.nonce,
        errorKind: 'fatal',
        message,
      });
    },
    [post]
  );

  // Load Stripe with the booking-origin (SPA) publishable key.
  useEffect(() => {
    if (!paramsRef.current) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await loadStripe(Config.data.stripe.key);
        if (cancelled) return;
        // test hook: see HOS-1822 (block js.stripe.com to force this fatal path)
        if (!s) {
          reportFatal('Unable to load the payment form.');
          return;
        }
        setStripe(s);
      } catch {
        // test hook: see HOS-1822 (block js.stripe.com to force this fatal path)
        if (!cancelled) reportFatal('Unable to load the payment form.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportFatal]);

  // Handshake: announce readiness, then accept the host's init (client secret).
  useEffect(() => {
    const params = paramsRef.current;
    if (!params) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== params.parentOrigin) return;
      const msg = parseEmbedPayMessage(event.data, params.nonce);
      if (!msg || msg.type !== 'init') return;
      setInit(msg);
    };

    window.addEventListener('message', onMessage);
    post({ source: EMBED_PAY_SOURCE, type: 'ready', nonce: params.nonce });
    return () => window.removeEventListener('message', onMessage);
  }, [post]);

  // Report height so the host can size the iframe to the form (responsive).
  useEffect(() => {
    const el = containerRef.current;
    const params = paramsRef.current;
    if (!el || !params) return;
    const report = () =>
      post({
        source: EMBED_PAY_SOURCE,
        type: 'resize',
        nonce: params.nonce,
        height: Math.ceil(el.getBoundingClientRect().height),
      });
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [post, init, stripe, fatal]);

  const brandRef = useRef<string>('Card');

  const handleSubmit = useCallback(
    async ({ stripe: s, cardNumberElement, billingDetails }: CardSubmitArgs) => {
      const params = paramsRef.current;
      if (!params || !init) return { error: 'Payment session not ready.' };

      const { setupIntent, error } = await s.confirmCardSetup(init.clientSecret, {
        payment_method: { card: cardNumberElement, billing_details: billingDetails },
      });

      if (error) {
        // Recoverable card problem (decline, invalid number, etc.) — keep it
        // INLINE in the card form so the member can correct and resubmit.
        // test hook: see HOS-1822 (decline test card to force this inline path)
        return { error: error.message ?? 'Card was not accepted.' };
      }

      const paymentMethod = setupIntent?.payment_method;
      const paymentMethodId =
        typeof paymentMethod === 'string' ? paymentMethod : (paymentMethod?.id ?? '');
      if (!paymentMethodId) {
        // confirmCardSetup resolved without an error but yielded no usable
        // payment method — non-recoverable. Escalate to the host for Retry.
        // test hook: see HOS-1822
        reportFatal('Card could not be saved. Please try again.');
        return undefined;
      }

      let brand = brandRef.current;
      let last4 = '****';
      if (paymentMethod && typeof paymentMethod === 'object') {
        brand = paymentMethod.card?.brand ?? brand;
        last4 = paymentMethod.card?.last4 ?? last4;
      }

      post({
        source: EMBED_PAY_SOURCE,
        type: 'success',
        nonce: params.nonce,
        paymentMethodId,
        brand,
        last4,
      });
      // Success is terminal for the iframe; keep the button in its submitting
      // state (return no error) while the host advances the overlay.
      return undefined;
    },
    [init, post, reportFatal]
  );

  return (
    <div ref={containerRef} style={frameWrapperStyle}>
      {fatal ? (
        <p style={messageStyle}>{fatal}</p>
      ) : !stripe || !init ? (
        <p style={messageStyle}>Loading secure payment…</p>
      ) : (
        <Elements stripe={stripe} options={STRIPE_OPTIONS}>
          <CardFields
            onSubmit={handleSubmit}
            submitLabel="Add Credit Card"
            submittingLabel="Adding…"
            defaultFirstName={init.firstName}
            defaultLastName={init.lastName}
            email={init.email}
            onCardBrandChange={(b) => {
              brandRef.current = b;
            }}
          />
        </Elements>
      )}
    </div>
  );
};

const frameWrapperStyle: React.CSSProperties = {
  fontFamily: "'Sailec', sans-serif",
  padding: 4,
  background: 'transparent',
  boxSizing: 'border-box',
};

const messageStyle: React.CSSProperties = {
  fontFamily: "'Sailec', sans-serif",
  color: '#8d8d8d',
  fontSize: 15,
  padding: '12px 4px',
  margin: 0,
};

export default EmbeddedPaymentFrame;
